import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useCreatePixRenewalOrderMutation,
  useMyCpmWalletQuery,
  useMyDataQuery,
} from '@/generated/graphql';
import { authAxios } from '@/lib/axios';
import PixRenewalModal from '@/components/PixRenewalModal';
import { RenewedCycleNotice } from '@/components/billing/RenewedCycleNotice';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useHomeRoute } from '@/hooks/use-home-route';
import { Clock, Copy, Ticket, QrCode, AlertCircle, Loader2, CheckCircle, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import { resolveRenewalCouponLink } from '@/lib/billing/resolveRenewalCouponLink';
import { currentEmbeddedApp } from '@/lib/app-embed/readEmbeddedApp';
import { canGenerateRenewalCode, isLivePixOrder } from '@/lib/billing/invoiceRenewalCode';
import { waitForRenewalApplied } from '@/lib/billing/waitForRenewalApplied';
import { refetchAllMyData } from '@/lib/apollo/refetchAllMyData';
import type { WalletRenewalSubscriptionLike } from '@/lib/billing/resolveWalletRenewalEligibility';

interface PixPaymentResponse {
  status: string;
  pix_provider_tid: string;
  qr_code: string;
  qr_code_url: string;
  expires_at: string;
  id: string;
  transaction_type: string;
  gateway_id: string;
  amount: number;
  success: boolean;
  created_at: string;
  updated_at: string;
  gateway_response: Record<string, any>;
  antifraud_response: Record<string, any>;
  metadata: Record<string, any>;
}

const Invoice: React.FC = () => {
  const {
    data: myData,
    loading: userLoading,
    refetch: refetchMyData,
  } = useMyDataQuery();
  const { data: walletData, refetch: refetchWallet } = useMyCpmWalletQuery();
  const [pixData, setPixData] = useState<PixPaymentResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [isPixRenewalModalOpen, setIsPixRenewalModalOpen] = useState(false);
  const [renewalVariant, setRenewalVariant] = useState<'pix' | 'grant'>('pix');
  const [couponLinkCode, setCouponLinkCode] = useState<string | null>(null);
  const [couponFieldRequested, setCouponFieldRequested] = useState(false);
  const [orderReloadKey, setOrderReloadKey] = useState(0);
  const [generatedOrderId, setGeneratedOrderId] = useState<string | null>(null);
  const [paidOrderId, setPaidOrderId] = useState<string | null>(null);
  // The paid renewal is applied (or the wait gave up): decide on the notice.
  const [renewalSettled, setRenewalSettled] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [createPixRenewalOrder] = useCreatePixRenewalOrderMutation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { route: homeRoute } = useHomeRoute();
  // Store rules forbid paying on the web inside the mobile app: details only.
  const embedded = currentEmbeddedApp() !== null;

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const attemptCountRef = useRef(0);
  const fetchedOrderIdRef = useRef<string | null>(null);
  const handledCouponParamRef = useRef<string | null>(null);

  const subscriptionData = myData?.myData?.subscription;
  const storedOrderId = subscriptionData?.pagarme_last_order_id;
  // A code generated on this page wins until the stored order changes. A paid
  // order stays on screen after the renewal clears the stored one.
  const lastOrderId = generatedOrderId ?? storedOrderId ?? paidOrderId;
  // A deferred reset (still set) means the renewal is already paid, and a plan
  // without a PIX renewal price can't be renewed here: no new code to pay.
  const canGenerateCode = canGenerateRenewalCode({ subscription: subscriptionData, embedded, isPaid });
  const canUseWallet = (walletData?.myCpmWallet?.available_cents ?? 0) > 0;

  const handleRenewalSuccess = async () => {
    setIsPolling(false);
    await Promise.allSettled([refetchMyData(), refetchWallet()]);
    // Reload whatever order the refreshed subscription points at, even when
    // pagarme_last_order_id did not change.
    fetchedOrderIdRef.current = null;
    setOrderReloadKey((key) => key + 1);
  };

  const couponParam = searchParams.get('coupon');
  const subscriptionLoaded = !userLoading && !!myData?.myData;

  const handleRenewalModalOpenChange = (open: boolean) => {
    setIsPixRenewalModalOpen(open);
    if (!open) {
      setCouponLinkCode(null);
      setCouponFieldRequested(false);
    }
  };

  useEffect(() => {
    setGeneratedOrderId(null);
  }, [storedOrderId]);

  // Pagar.me reports "paid" before our webhook applies the renewal; refetch
  // until it has, so the notice is decided from the applied subscription.
  // Then refresh every other myData observer (the Sidebar holds its own).
  const settlePaidRenewal = async (orderId: string, before: typeof subscriptionData) => {
    setRenewalSettled(false);
    await waitForRenewalApplied({
      orderId,
      before,
      refetch: async () => (await refetchMyData())?.data?.myData?.subscription,
    });
    setRenewalSettled(true);
    await Promise.resolve(refetchAllMyData()).catch((err) => {
      console.error('Error refreshing myData after the renewal:', err);
    });
  };

  const handleGenerateCode = async () => {
    setGenerating(true);
    try {
      const { data } = await createPixRenewalOrder();
      const order = data?.createPixRenewalOrder;
      if (!order?.pix_code) throw new Error('missing_pix_code');
      const now = new Date().toISOString();
      // Mark the order as loaded so the order effect does not refetch it.
      fetchedOrderIdRef.current = order.id;
      attemptCountRef.current = 0;
      setError(null);
      setIsPaid(false);
      setPixData({
        id: order.id,
        status: order.status,
        pix_provider_tid: '',
        qr_code: order.pix_code,
        qr_code_url: order.pix_qr_code_url ?? '',
        expires_at: order.expires_at ?? now,
        transaction_type: 'pix',
        gateway_id: '',
        amount: order.amount,
        success: true,
        created_at: now,
        updated_at: now,
        gateway_response: {},
        antifraud_response: {},
        metadata: {},
      });
      setGeneratedOrderId(order.id);
      setIsPolling(order.status === 'waiting_payment' || order.status === 'pending');
    } catch (err) {
      console.error('Error generating PIX renewal order:', err);
      toast({
        title: t('common.error'),
        description: t('invoice.pixPaymentError'),
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  const generateCodeButton = (
    <Button type="button" className="w-full" onClick={handleGenerateCode} disabled={generating}>
      {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <QrCode className="w-4 h-4 mr-2" />}
      {t('invoice.generatePixCode')}
    </Button>
  );

  // Load the order whenever it changes (a coupon regenerates the PIX order).
  useEffect(() => {
    if (!lastOrderId) {
      fetchedOrderIdRef.current = null;
      return;
    }
    if (fetchedOrderIdRef.current === lastOrderId) return;
    fetchedOrderIdRef.current = lastOrderId;
    setIsPolling(false);
    setPixData(null);
    setIsPaid(false);
    attemptCountRef.current = 0;
    fetchPixData();
  }, [lastOrderId, orderReloadKey]);

  // Shareable `/invoice?coupon=CODE` link: handled once per code after myData loads.
  useEffect(() => {
    if (!couponParam || !subscriptionLoaded) return;
    if (handledCouponParamRef.current === couponParam) return;
    handledCouponParamRef.current = couponParam;

    const result = resolveRenewalCouponLink({
      subscription: subscriptionData as WalletRenewalSubscriptionLike | null | undefined,
      code: couponParam,
      now: new Date(),
    });

    if (result?.action === 'plans') {
      navigate(`/plans?coupon=${encodeURIComponent(result.code)}`, { replace: true });
      return;
    }

    if (result?.action === 'apply') {
      setRenewalVariant(result.variant);
      setCouponLinkCode(result.code);
      setIsPixRenewalModalOpen(true);
    } else if (result?.action === 'unsupported') {
      toast({
        title: t('renewalCoupon.unsupportedTitle'),
        description: t('renewalCoupon.unsupportedDescription'),
      });
    }
    // Every handled (or unusable) code leaves the URL.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('coupon');
        return next;
      },
      { replace: true },
    );
  }, [couponParam, subscriptionLoaded]);

  // Polling effect
  useEffect(() => {
    if (!isPolling) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    // Start polling every 5 seconds
    pollingIntervalRef.current = setInterval(() => {
      attemptCountRef.current++;

      // Stop polling after 120 attempts (10 minutes)
      if (attemptCountRef.current >= 120) {
        setIsPolling(false);
        toast({
          title: t('invoice.timeLimitReached'),
          description: t('invoice.timeLimitDescription'),
          variant: "destructive",
        });
        return;
      }

      fetchPixData(true);
    }, 5000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [isPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const fetchPixData = async (isPollingCall = false) => {
    if (!lastOrderId) return;
    const orderId = lastOrderId;
    // A coupon can replace the order while this request is in flight.
    const isStale = () => fetchedOrderIdRef.current !== orderId;

    if (!isPollingCall) {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await authAxios.get(`/api/subscriptions/pix/${orderId}`);
      if (isStale()) return;
      const newPixData = response.data;
      setPixData(newPixData);

      // Check if payment is completed
      if (newPixData.status === 'paid') {
        setIsPaid(true);
        setIsPolling(false);
        setPaidOrderId(orderId);
        void settlePaidRenewal(orderId, subscriptionData);
        toast({
          title: t('invoice.paymentConfirmedToastTitle'),
          description: t('invoice.paymentConfirmedToastDescription'),
        });
      } else if (!isLivePixOrder(newPixData, new Date())) {
        // Expired, cancelled or failed, or past its PIX expiry whatever the
        // status says: nothing left to wait for.
        setIsPolling(false);
      } else if (!isPaid) {
        // Start polling if payment is still waiting
        setIsPolling(true);
      }
    } catch (err: any) {
      if (isStale()) return;
      const errorMessage = err?.response?.data?.message || t('invoice.pixPaymentError');
      setError(errorMessage);

      if (!isPollingCall) {
        toast({
          title: t('common.error'),
          description: errorMessage,
          variant: "destructive",
        });
      }

      // Stop polling on error
      setIsPolling(false);
    } finally {
      if (!isPollingCall) {
        setLoading(false);
      }
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: t('invoice.copied'),
        description: t('invoice.pixCodeCopied'),
      });
    } catch {
      toast({
        title: t('common.error'),
        description: t('invoice.pixCodeCopyError'),
        variant: "destructive",
      });
    }
  };

  const formatExpirationDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAmount = (amount: number) => {
    return (amount / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'waiting_payment':
        return t('invoice.statusWaitingPayment');
      case 'paid':
        return t('invoice.statusPaid');
      case 'expired':
        return t('invoice.statusExpired');
      case 'canceled':
        return t('invoice.statusCanceled');
      default:
        return status;
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'waiting_payment':
        return 'secondary';
      case 'paid':
        return 'default';
      case 'expired':
      case 'canceled':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  if (userLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-gray-800 border-gray-700">
          <CardContent className="text-center py-8">
            <Loader2 className="animate-spin h-8 w-8 text-primary mx-auto mb-4" />
            <p className="text-gray-300">{t('invoice.loadingUserData')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!lastOrderId) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-gray-800 border-gray-700">
          <CardContent className="text-center py-8">
            <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">
              {t('invoice.noPendingPayment')}
            </h2>
            <p className="text-gray-300 mb-4">
              {t('invoice.noPendingPaymentDescription')}
            </p>
            <RenewedCycleNotice subscription={subscriptionData} onStarted={refetchMyData} className="mb-4" />
            {canGenerateCode && <div className="mb-3">{generateCodeButton}</div>}
            <Button
              variant="outline"
              onClick={() => window.history.back()}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              {t('common.back')}
            </Button>
          </CardContent>
        </Card>
        <PixRenewalModal
        open={isPixRenewalModalOpen}
        onOpenChange={handleRenewalModalOpenChange}
        onSuccess={handleRenewalSuccess}
        autoGenerate={!!couponLinkCode && renewalVariant === 'pix'}
        variant={renewalVariant}
        initialCouponCode={couponLinkCode}
        openCouponField={couponFieldRequested}
        onCouponApplied={refetchMyData}
      />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-4">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            {t('invoice.title')}
          </h1>
          <p className="text-gray-400">
            {t('invoice.subtitle')}
          </p>
        </div>

        {loading && (
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="text-center py-8">
              <Loader2 className="animate-spin h-8 w-8 text-primary mx-auto mb-4" />
              <p className="text-gray-300">{t('invoice.loadingPaymentData')}</p>
            </CardContent>
          </Card>
        )}

        {error && (
          <Card className="bg-red-900/20 border-red-500/50">
            <CardContent className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-red-500 mb-2">
                {t('invoice.paymentLoadError')}
              </h2>
              <p className="text-red-300 mb-4">{error}</p>
              <Button
                variant="outline"
                onClick={() => fetchPixData()}
                className="border-red-500 text-red-500 hover:bg-red-500/10"
              >
                {t('invoice.tryAgain')}
              </Button>
            </CardContent>
          </Card>
        )}

        {pixData && (
          <div className="space-y-6">
            {/* Status e Informações Gerais */}
            <Card className={`${isPaid ? 'bg-green-900/20 border-green-500/50' : 'bg-gray-800 border-gray-700'}`}>
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {t('invoice.paymentStatus')}
                    {isPaid && <CheckCircle className="w-5 h-5 text-green-500" />}
                    {isPolling && <RefreshCw className="w-4 h-4 text-primary animate-spin" />}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={getStatusVariant(pixData.status)}>
                      {getStatusText(pixData.status)}
                    </Badge>
                    {isPolling && (
                      <Badge variant="outline" className="text-primary border-primary">
                        {t('common.verifying')}
                      </Badge>
                    )}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-400">{t('invoice.amount')}</p>
                    <p className="text-lg font-semibold text-white">
                      {formatAmount(pixData.amount)}
                    </p>
                  </div>
                  <div />
                  <div>
                    <p className="text-sm text-gray-400">{t('invoice.createdAt')}</p>
                    <p className="text-sm text-white">
                      {formatExpirationDate(pixData.created_at)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {t('invoice.expiresAt')}
                    </p>
                    <p className="text-sm text-yellow-400 font-medium">
                      {formatExpirationDate(pixData.expires_at)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {canGenerateCode &&
              pixData.status !== 'paid' &&
              !isLivePixOrder(pixData, new Date()) &&
              generateCodeButton}

            {!embedded && canUseWallet && !isPaid && (
              <Button
                type="button"
                className="w-full"
                onClick={() => setIsPixRenewalModalOpen(true)}
              >
                {t('account.renewWithWallet')}
              </Button>
            )}

            {!embedded && !isPaid && (
              <Button
                type="button"
                variant="outline"
                className="w-full border-gray-600 text-gray-300 hover:bg-gray-700"
                onClick={() => {
                  setCouponFieldRequested(true);
                  setIsPixRenewalModalOpen(true);
                }}
              >
                <Ticket className="w-4 h-4 mr-2" />
                {t('renewalCoupon.toggle')}
              </Button>
            )}

            {/* QR Code */}
            {!embedded && (<>
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <QrCode className="w-5 h-5" />
                  {t('invoice.qrCodeTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <div className="bg-white p-4 rounded-lg inline-block mb-4">
                  <img
                    src={pixData.qr_code_url}
                    alt={t('invoice.qrCodeAlt')}
                    className="w-48 h-48 mx-auto"
                  />
                </div>
                <p className="text-gray-400 text-sm">
                  {t('invoice.qrCodeInstructions')}
                </p>
              </CardContent>
            </Card>

            {/* Código PIX Copiável */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Copy className="w-5 h-5" />
                  {t('invoice.pixCodeTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-gray-900 p-4 rounded-lg border">
                  <p className="text-sm font-mono text-gray-300 break-all leading-relaxed">
                    {pixData.qr_code}
                  </p>
                </div>
                <Button
                  onClick={() => copyToClipboard(pixData.qr_code)}
                  className="w-full"
                  variant="outline"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  {t('invoice.copyPixCode')}
                </Button>
                <div className="text-center">
                  <p className="text-xs text-gray-400">
                    {t('invoice.pixCodeCopyHint')}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Instruções */}
            <Card className="bg-primary/20 border-primary/50">
              <CardHeader>
                <CardTitle className="text-primary">{t('invoice.howToPay')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2 text-sm text-primary-200">
                  <p><strong>1.</strong> {t('invoice.howToPayStep1')}</p>
                  <p><strong>2.</strong> {t('invoice.howToPayStep2')}</p>
                  <p><strong>3.</strong> {t('invoice.howToPayStep3')}</p>
                  <p><strong>4.</strong> {t('invoice.howToPayStep4')}</p>
                  <p><strong>5.</strong> {t('invoice.howToPayStep5')}</p>
                </div>
                <div className="mt-4 p-3 bg-primary/30 rounded-lg">
                  <p className="text-xs text-primary-300">
                    {t('invoice.howToPayNote')}
                  </p>
                </div>
              </CardContent>
            </Card>

            </>)}

            {/* Success Message */}
            {isPaid && (
              <Card className="bg-green-900/20 border-green-500/50">
                <CardContent className="text-center py-8">
                  <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-green-500 mb-2">
                    {t('invoice.paymentConfirmed')}
                  </h2>
                  <p className="text-green-300 mb-4">
                    {t('invoice.paymentConfirmedDescription')}
                  </p>
                  {renewalSettled && (
                    <RenewedCycleNotice subscription={subscriptionData} onStarted={refetchMyData} className="mb-4" />
                  )}
                  <Button
                    onClick={() => window.location.href = homeRoute}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {t('invoice.goToProjects')}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Manual Refresh Button */}
            {!isPaid && (
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="text-center py-6">
                  <div className="space-y-4">
                    {isPolling ? (
                      <div className="text-primary">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                        <p className="text-sm">{t('invoice.autoCheckingStatus')}</p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-gray-300 text-sm mb-3">
                          {t('invoice.autoCheckPaused')}
                        </p>
                        <Button
                          onClick={() => fetchPixData()}
                          variant="outline"
                          className="border-gray-600 text-gray-300 hover:bg-gray-700"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          {t('invoice.checkStatus')}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
      <PixRenewalModal
        open={isPixRenewalModalOpen}
        onOpenChange={handleRenewalModalOpenChange}
        onSuccess={handleRenewalSuccess}
        autoGenerate={!!couponLinkCode && renewalVariant === 'pix'}
        variant={renewalVariant}
        initialCouponCode={couponLinkCode}
        openCouponField={couponFieldRequested}
        onCouponApplied={refetchMyData}
      />
    </div>
  );
};

export default Invoice;
